import { Alert, Box, DialogContent } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useCollections, useMetaData } from '../../api';
import CollectionField from '../../forms/collection-field/CollectionField';
import ExerciseField from '../../forms/exercise-field/ExerciseField';
import ExercisesTagsField from '../../forms/exercises-tags-field/ExercisesTagsField';
import LayoutField from '../../forms/layout-field/LayoutField';
import ModuleField from '../../forms/module-filed/ModuleField';
import SimulatorField from '../../forms/simulator-field/SimulatorField';
import VisibilityField from '../../forms/visibility-field/VisibilityField';
import KSimHeader from '../../k-sim-header/KSimHeader';
import { ExerciseFile, ExerciseMetaData, ExerciseType, Simulator, TechnologyPlatform } from '../../types';
import { Collection } from '../../types/collection';
import { Module } from '../../types/module';
import StudentPositions from '../student-positions/StudentPositions';
import { ExerciseFormData } from './CreateExerciseDialog';

export type ExerciseStepProps = UseFormReturn<ExerciseFormData> & {
  actions: ReactNode;
  handleNext: SubmitHandler<ExerciseFormData>;
  simulators?: Simulator[];
  stepper: ReactNode;
};

const ExerciseStep = ({
  actions,
  clearErrors,
  control,
  handleNext,
  handleSubmit,
  setError,
  setValue,
  simulators,
  stepper,
  watch,
}: ExerciseStepProps) => {
  const [exerciseId, exercisesIds, exercisesMetaData, exercises, simulatorId, type, vessel, collectionId, moduleId] =
    watch([
      'exerciseId',
      'exercisesIds',
      'exercisesMetaData',
      'exercises', // TODO merge exercisesMetaData and exercises
      'simulator',
      'type',
      'vessel',
      'collectionId',
      'moduleId',
    ]);

  const { collections, isCollectionsError, isCollectionsLoading } = useCollections(simulatorId);
  useEffect(() => {
    if (collections) {
      setValue(
        'exercises',
        collections
          ?.map((collection: Collection) => collection.modules)
          .flat()
          .map((module: Module) => module.exercises)
          .flat(),
      );
    }
  }, [collections]);

  const simulatorType = simulators?.find(simulator => simulator.id === watch('simulator'))?.type;
  const simulatorPlatform = simulators?.find(simulator => simulator.id === watch('simulator'))?.platform;
  const isSelfStudy = type === ExerciseType.SelfStudy;
  const [exercisesIdsError, setExercisesIdsError] = useState({
    show: false,
    message: `Only exercises with only one student position can be scheduled in a bulk operation. The selected exercise in red does not meet this requirement and must be deleted to continue.`,
  });

  const { isLoading, isExerciseMetaDataError, getMetaData } = useMetaData((exercisesMetaData: ExerciseMetaData[]) => {
    setValue('exercisesMetaData', exercisesMetaData);

    //TODO refactor make util for this logic, and replace the logic for rc/forms/exercises-tags-field/ExercisesTagsField.tsx line:62
    if (
      exercisesMetaData?.some(
        exercise =>
          exercise.metaData.length === 0 ||
          (exercisesIds &&
            exercisesIds.length > 1 &&
            (exercise.metaData.length !== 1 || exercise.metaData[0].students.length !== 1)),
      )
    ) {
      setExercisesIdsError({
        ...exercisesIdsError,
        show: true,
      });

      setError('exercisesIds', {
        message: exercisesIdsError.message,
      });
    }
  });

  useEffect(() => {
    setValue('exercisesIds', null);
    setValue('exerciseId', null);
    setValue('exercisesMetaData', null);
    setValue('vessel', null);
    setValue('layout', null);
  }, [type]);

  useEffect(() => {
    setValue('exercisesIds', null);
    setValue('exerciseId', null);
  }, [simulatorId]);

  useEffect(() => {
    setValue('exercisesIds', null);
    setValue('exerciseId', null);
    clearErrors('exercisesIds');
  }, [collectionId, moduleId]);

  useEffect(() => {
    setValue('exercisesMetaData', []);
    setValue('vessel', null);
    setValue('studentEnvironment', null);
    setExercisesIdsError({
      ...exercisesIdsError,
      show: false,
    });

    if (isSelfStudy && simulatorPlatform === TechnologyPlatform.Spirit && exercisesIds?.length) {
      getMetaData(exercisesIds);
    }
  }, [exercisesIds]);

  const modules = collections?.find(collection => collection.id === collectionId)?.modules;
  const selectedExercise = exercises?.find(
    (exercise: ExerciseFile) => exercise.id === (exerciseId || (exercisesIds && exercisesIds[0])),
  );
  const moduleExercises = modules?.find(module => module.id === moduleId)?.exercises;
  useEffect(() => {
    if (isSelfStudy) {
      const ids = moduleExercises?.map(exercise => exercise.id);
      ids && setValue('exercisesIds', ids);
    }
  }, [moduleId]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit((data, e) => {
        if (exercisesIdsError.show) {
          setError('exercisesIds', {
            message: exercisesIdsError.message,
          });
          e?.preventDefault();
        } else {
          handleNext(data);
        }
      })}
      noValidate
      autoComplete="off"
      sx={{ display: 'contents' }}
    >
      <DialogContent>
        {stepper}
        <Box display="flex" flexDirection="column" gap={3}>
          <KSimHeader exerciseType={type} mobileOnly simulatorType={simulatorType} sx={{ height: '153px' }} />
          <SimulatorField control={control} name="simulator" simulators={simulators ?? []} exerciseType={type} />

          <CollectionField
            collections={collections}
            control={control}
            loading={!!simulatorId && !collections}
            name="collectionId"
          />

          <ModuleField control={control} modules={modules} name="moduleId" />

          {isSelfStudy ? (
            <>
              <ExercisesTagsField
                control={control}
                disabled={isLoading || !simulatorId || isCollectionsLoading}
                exercises={moduleExercises || exercises}
                loading={isLoading}
                name="exercisesIds"
                platform={simulatorPlatform}
                watch={watch}
              />
              {exercisesMetaData?.length === 1 && exercisesMetaData[0]?.metaData.length ? (
                <>
                  <StudentPositions
                    control={control}
                    exerciseId={exercisesMetaData[0].id}
                    studentEnvironment={exercisesMetaData[0].metaData}
                    setValue={setValue}
                    vessel={vessel}
                    clearErrors={clearErrors}
                  />

                  {selectedExercise?.layouts?.length ? (
                    <LayoutField control={control} layouts={selectedExercise.layouts} name="layout" />
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <ExerciseField
              control={control}
              disabled={!exercises || !simulatorId}
              exercises={moduleExercises || exercises}
              name="exerciseId"
            />
          )}

          {/* TODO: only show when an assessment is present in the exercise file */}
          <VisibilityField control={control} name="visibility" />
          {isCollectionsError && <Alert severity="error">An issue prevented the list of exercises to be fetched</Alert>}
          {isExerciseMetaDataError && (
            <Alert severity="error">
              Something went wrong and we couldn&lsquo;t load your student positions. Please try again
            </Alert>
          )}
        </Box>
      </DialogContent>
      {actions}
    </Box>
  );
};

export default ExerciseStep;
